import { Button, Card, Link, makeStyles, Text, tokens } from '@fluentui/react-components';
import { Checkmark20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { SpirisPrompt } from '../../../libs/models/ChatMessage';
import { SpirisService } from '../../../libs/services/SpirisService';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';

const useStyles = makeStyles({
    card: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderLeft: `4px solid ${tokens.colorPaletteMarigoldBorderActive}`,
        padding: tokens.spacingVerticalM,
        marginTop: tokens.spacingVerticalS,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
    },
    action: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
    },
    actionTitle: {
        fontWeight: tokens.fontWeightSemibold,
    },
    arguments: {
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: tokens.spacingHorizontalM,
        rowGap: tokens.spacingVerticalXXS,
        margin: 0,
    },
    argumentRow: {
        display: 'contents',
    },
    argumentName: {
        color: tokens.colorNeutralForeground3,
    },
    argumentValue: {
        margin: 0,
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-wrap',
    },
    buttons: {
        display: 'flex',
        gap: tokens.spacingHorizontalS,
    },
    status: {
        color: tokens.colorNeutralForeground2,
    },
    error: {
        color: tokens.colorPaletteRedForeground1,
    },
});

const decidedStatuses = ['approved', 'denied', 'expired'];

const collapsedValueLength = 300;

// Zero-width, soft-hyphen, bidi-control, variation-selector and Unicode tag characters can hide text or
// reorder what a value appears to say (e.g. make an account number read differently), so they are shown as
// visible code points instead.
const invisibleCharacters =
    /[\u00AD\u034F\u061C\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFE00-\uFE0F\uFEFF\u{E0000}-\u{E007F}\u{E0100}-\u{E01EF}]/gu;

export const revealInvisibleCharacters = (value: string): string =>
    value.replace(
        invisibleCharacters,
        (character) => `[U+${(character.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0')}]`,
    );

const isLong = (value: string) => revealInvisibleCharacters(value).length > collapsedValueLength;

interface ArgumentValueProps {
    value: string;
    className: string;
    expanded: boolean;
    onToggle: () => void;
}

// The full value is what runs, so it is always reachable; long values are only folded to keep the card short.
const ArgumentValue: React.FC<ArgumentValueProps> = ({ value, className, expanded, onToggle }) => {
    const { t } = useTranslation();
    const shown = revealInvisibleCharacters(value);
    const long = isLong(value);

    return (
        <dd className={className}>
            {long && !expanded ? `${shown.slice(0, collapsedValueLength)}…` : shown}
            {long && (
                <>
                    {' '}
                    <Link
                        as="button"
                        aria-expanded={expanded}
                        onClick={onToggle}
                        data-testid="spiris-approval-toggle-value"
                    >
                        {expanded ? t('spiris.approval.showLess') : t('spiris.approval.showMore')}
                    </Link>
                </>
            )}
        </dd>
    );
};

interface SpirisApprovalCardProps {
    chatId: string;
    prompt: SpirisPrompt;
}

type State = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'submitted' } | { kind: 'error' };

export const SpirisApprovalCard: React.FC<SpirisApprovalCardProps> = ({ chatId, prompt }) => {
    const styles = useStyles();
    const { t } = useTranslation();
    const token = useAuth().user?.access_token;
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const [state, setState] = useState<State>({ kind: 'idle' });
    const [expandedValues, setExpandedValues] = useState<ReadonlySet<string>>(new Set());

    if (!token || !prompt.approvalId) {
        return null;
    }

    const approvalId = prompt.approvalId;
    // Only a pending approval can be answered; any other status, even one this build doesn't know, can't.
    const pending = prompt.status === 'pending';
    const locked = state.kind === 'submitting' || state.kind === 'submitted';
    const valueKey = (actionIndex: number, argumentIndex: number) =>
        `${actionIndex.toString()}-${argumentIndex.toString()}`;
    // The part of a folded value the user hasn't seen is still written, so every folded value must be
    // opened before Approve is possible. Deny never needs it.
    const unseenValues = prompt.actions.some((action, actionIndex) =>
        action.arguments.some(
            (argument, argumentIndex) =>
                isLong(argument.value) && !expandedValues.has(valueKey(actionIndex, argumentIndex)),
        ),
    );
    const toggleValue = (key: string) => {
        setExpandedValues((current) => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const decide = async (approved: boolean) => {
        setState({ kind: 'submitting' });
        try {
            await new SpirisService(token).decideApprovalAsync(chatId, approvalId, approved, hostContext);
            setState({ kind: 'submitted' });
        } catch {
            // An already-decided or expired approval also lands here; its status arrives over SignalR.
            setState({ kind: 'error' });
        }
    };

    return (
        <Card className={styles.card} data-testid="spiris-approval-card">
            <Text className={styles.title}>{t('spiris.approval.title')}</Text>
            {prompt.actions.map((action, index) => (
                <div key={`${action.title}-${index.toString()}`} className={styles.action}>
                    <Text className={styles.actionTitle}>{revealInvisibleCharacters(action.title)}</Text>
                    {action.arguments.length > 0 && (
                        <dl className={styles.arguments}>
                            {action.arguments.map((argument, argumentIndex) => (
                                <div
                                    key={`${argument.name}-${argumentIndex.toString()}`}
                                    className={styles.argumentRow}
                                >
                                    <dt className={styles.argumentName}>{revealInvisibleCharacters(argument.name)}</dt>
                                    <ArgumentValue
                                        value={argument.value}
                                        className={styles.argumentValue}
                                        expanded={expandedValues.has(valueKey(index, argumentIndex))}
                                        onToggle={() => {
                                            toggleValue(valueKey(index, argumentIndex));
                                        }}
                                    />
                                </div>
                            ))}
                        </dl>
                    )}
                </div>
            ))}
            {!pending ? (
                <Text className={styles.status} aria-live="polite" data-testid="spiris-approval-status">
                    {decidedStatuses.includes(prompt.status)
                        ? t(`spiris.approval.status.${prompt.status}`)
                        : t('spiris.approval.status.closed')}
                </Text>
            ) : (
                <div className={styles.buttons}>
                    <Button
                        appearance="primary"
                        icon={<Checkmark20Regular />}
                        disabled={locked || unseenValues}
                        onClick={() => {
                            void decide(true);
                        }}
                        data-testid="spiris-approval-approve"
                    >
                        {t('spiris.approval.approve')}
                    </Button>
                    <Button
                        icon={<Dismiss20Regular />}
                        disabled={locked}
                        onClick={() => {
                            void decide(false);
                        }}
                        data-testid="spiris-approval-deny"
                    >
                        {t('spiris.approval.deny')}
                    </Button>
                </div>
            )}
            {pending && unseenValues && (
                <Text className={styles.status} data-testid="spiris-approval-unseen">
                    {t('spiris.approval.showAllFirst')}
                </Text>
            )}
            {/* Once decided the status wins: a request that failed after the claim still resumed the run. */}
            {state.kind === 'error' && pending && <Text className={styles.error}>{t('spiris.approval.error')}</Text>}
        </Card>
    );
};
